import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "src/components/Pagination/Pagination";
import { DEFAULT_ADMIN_PAGE_SIZE } from "src/constants";
import Page from "src/layouts/Page";
import ContentTable, {
	ContentTableColumn
} from "src/components/ContentTable/ContentTable";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useRoles from "../../hooks/useRoles";
import useDeleteRole from "../../hooks/useDeleteRole";
import { mapRolesToContentTableItems } from "../../utils/mapRolesToContentTableItems";
import { isSeededRole } from "../../constants/permissionCategories";

const roleColumns: ContentTableColumn[] = [
	{
		key: "permissionCount",
		label: "Permissions",
		render: (item) => {
			const count = Number(item.data?.permissionCount ?? 0);
			return (
				<span className="text-sm text-muted-foreground">
					{count} {count === 1 ? "permission" : "permissions"}
				</span>
			);
		}
	}
];

const Permissions = () => {
	const navigate = useNavigate();
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const [itemToDelete, setItemToDelete] = useState<{
		id: string;
		name: string;
	} | null>(null);

	const { data: roles, isLoading, isFetching, isError } = useRoles();
	const { mutate: deleteRole, isLoading: isDeleting } = useDeleteRole();

	const filtered = useMemo(() => {
		if (!roles) return [];
		if (!search) return roles;
		const lower = search.toLowerCase();
		return roles.filter((r) => r.name.toLowerCase().includes(lower));
	}, [roles, search]);

	const totalCount = filtered.length;
	const paged = filtered.slice(
		(page - 1) * DEFAULT_ADMIN_PAGE_SIZE,
		page * DEFAULT_ADMIN_PAGE_SIZE
	);
	const items = mapRolesToContentTableItems(paged);

	if (isError) {
		return (
			<Page>
				<p className="text-sm text-destructive">
					Something went wrong loading roles.
				</p>
			</Page>
		);
	}

	return (
		<Page>
			<ContentTable
				items={items}
				columns={roleColumns}
				isLoading={isLoading || isFetching}
				showCreatedBy={false}
				showLastUpdated={false}
				showOrder={false}
				showStatus={false}
				searchValue={search}
				onSearchChange={(value) => {
					setSearch(value);
					setPage(1);
				}}
				onRowClick={(item) => navigate(`/users/permissions/${item.id}`)}
				onDelete={(item) => {
					if (!isSeededRole(item.name)) {
						setItemToDelete({ id: String(item.id), name: item.name });
					}
				}}
				canDelete={(item) => !isSeededRole(item.name)}
				createPath="/users/permissions/create"
				createLabel="New Role"
				searchPlaceholder="Search roles..."
				nameColumnLabel="Role"
			/>

			<Pagination
				page={page}
				pageSize={DEFAULT_ADMIN_PAGE_SIZE}
				totalCount={totalCount}
				onPageChange={setPage}
				itemLabel="roles"
			/>

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete role?"
				description={`This will remove the "${itemToDelete?.name}" role. Users currently assigned this role will lose its permissions. This action cannot be undone.`}
				onCancel={() => {
					if (!isDeleting) setItemToDelete(null);
				}}
				cancelBtnProps={{ disabled: isDeleting }}
				onSubmit={() => {
					if (itemToDelete) {
						deleteRole(itemToDelete.id, {
							onSuccess: () => setItemToDelete(null)
						});
					}
				}}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default Permissions;
